/** Licensed users by how long ago they last signed in — now a real histogram.
 *
 *  Counts only — this panel never names anyone. Sign-in activity needs
 *  `AuditLog.Read.All`; without it the backend suppresses the buckets rather
 *  than reporting everyone as inactive, and this renders the reason instead.
 *
 *  Bars in the reclaim window (91d+ and never signed in) wear the warning
 *  token; the caption says so, so the colour is never the only carrier. A bar
 *  click drills to the admin-gated account list (admins) or opens the table
 *  twin with the exact counts (everyone else).
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { Alert, type DataGridColumnDef } from "@norican/react";

import { ChartCard, TABLE_TWIN_PARAM } from "@/components/board";
import type { InactivityBucket } from "@/lib/api";
import { useDrawerParam, useDrillNavigate } from "@/lib/drill";

import { formatSeats } from "./licence-format";
import { RECLAIMABLE_ACCOUNTS_PATH } from "./reclaimable-summary";

/** `?data=inactivity` opens this chart's table twin. */
const TWIN_ID = "inactivity";

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

/** Buckets past this point are the ones worth reclaiming. */
const STALE_BUCKET_IDS = new Set(["91-180", "180+", "never"]);

/** Status colours per bucket: warning marks the reclaim window, never a ramp. */
function bucketConfig(buckets: InactivityBucket[]): ChartConfig {
  return Object.fromEntries(
    buckets.map((bucket) => [
      bucket.id,
      {
        label: bucket.label,
        color:
          bucket.id === "unknown"
            ? "var(--norican-bg-muted)"
            : STALE_BUCKET_IDS.has(bucket.id)
              ? "var(--norican-warning)"
              : "var(--norican-accent)",
      },
    ]),
  );
}

const TWIN_COLUMNS: DataGridColumnDef<InactivityBucket>[] = [
  {
    id: "bucket",
    header: "Last sign-in",
    accessor: (bucket) => bucket.label,
    cell: (bucket) => <span className="text-fg">{bucket.label}</span>,
  },
  {
    id: "users",
    header: "Licensed users",
    numeric: true,
    accessor: (bucket) => bucket.users,
    cell: (bucket) => <span className="text-fg tabular-nums">{formatSeats(bucket.users)}</span>,
  },
];

interface InactivityHistogramProps {
  buckets: InactivityBucket[];
  signInDataAvailable: boolean;
  /** Whether the viewer holds the admin role — routes the reclaim drill. */
  isAdmin: boolean;
  generatedAt: string | null | undefined;
}

export function InactivityHistogram({
  buckets,
  signInDataAvailable,
  isAdmin,
  generatedAt,
}: InactivityHistogramProps) {
  const twin = useDrawerParam(TABLE_TWIN_PARAM);
  const navigateDrill = useDrillNavigate();

  return (
    <ChartCard
      title="Last sign-in"
      caption="Licensed users, counts only · amber marks the reclaim window (90d+)"
      since={generatedAt}
      staleAfterMs={STALE_AFTER_MS}
      tableTwin={{
        id: TWIN_ID,
        ariaLabel: "Licensed users per last-sign-in bucket",
        data: buckets,
        columns: TWIN_COLUMNS,
        getRowId: (bucket) => bucket.id,
      }}
    >
      {!signInDataAvailable ? (
        <Alert tone="warning" title="Sign-in activity is unavailable">
          The app registration is missing <strong>AuditLog.Read.All</strong>, so the last sign-in of
          a licensed user cannot be read. Seat counts on this board are unaffected; the idle-account
          estimate is not included until the scope is granted.
        </Alert>
      ) : buckets.length === 0 ? (
        <p className="text-fg-muted text-sm">No licensed users to report on.</p>
      ) : (
        <Chart.Container
          config={bucketConfig(buckets)}
          aria-label="Licensed users by time since last sign-in"
          style={{ height: 240 }}
        >
          <Chart.BarChart data={buckets} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
            <Chart.XAxis {...Chart.axisProps} dataKey="label" interval={0} />
            <Chart.YAxis {...Chart.axisProps} width={44} allowDecimals={false} />
            <Chart.Tooltip
              cursor={{ fill: "var(--norican-surface-hover)" }}
              content={
                <Chart.TooltipContent
                  formatter={(value) =>
                    `${formatSeats(typeof value === "number" ? value : Number(value))} users`
                  }
                />
              }
            />
            <Chart.Bar
              {...Chart.seriesDefaults}
              data={buckets}
              dataKey="users"
              colorKey="id"
              radius={[3, 3, 0, 0]}
              onClick={(item: { payload?: InactivityBucket }) => {
                const bucket = item.payload;
                if (bucket && STALE_BUCKET_IDS.has(bucket.id) && isAdmin) {
                  navigateDrill(RECLAIMABLE_ACCOUNTS_PATH);
                  return;
                }
                twin.open(TWIN_ID);
              }}
            />
          </Chart.BarChart>
        </Chart.Container>
      )}
    </ChartCard>
  );
}
